import { useMemo } from 'react';
import { formatInSemicolonString } from 'utils/formatUtils';
import { getTextOfAutomationError } from 'utils/businessLogic';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { CronoErrorCode } from 'crono-fe-common/types/enums/cronoErrorCode';
import { withMeta } from 'crono-fe-common/types/mutationMeta';
import usePatchTask from 'hooks/services/task/usePatchTask';
import { getVisibleAutomationError } from './taskErrorVisibility';

const automatableTypes = [
  TaskTodoType.EMAIL,
  TaskTodoType.LINKEDIN,
  TaskTodoType.IN_MAIL,
];

const errorsNotFixableByAutomating = [
  CronoErrorCode.AutomationFailedPreviousTaskPending,
  CronoErrorCode.AutomationFailedProspectNotActive,
  CronoErrorCode.AutomationFailedAccountNotActive,
];

interface TaskErrorsLabelProps {
  task: TaskTodo;
}

export const TaskErrorsLabel = ({ task }: TaskErrorsLabelProps) => {
  const { mutate: patchTask, isLoading: isPatchTaskLoading } = usePatchTask();

  const automationError = getVisibleAutomationError(task);
  const canAutomate =
    !!automationError &&
    !task.automatic &&
    automatableTypes.includes(task.type) &&
    !errorsNotFixableByAutomating.includes(automationError);

  const messages = useMemo(() => {
    const isInMail = task.type === TaskTodoType.IN_MAIL;
    return [task.taskError, automationError]
      .filter((error): error is CronoErrorCode => !!error)
      .map((error) => getTextOfAutomationError(error, isInMail));
  }, [task.taskError, task.type, automationError]);

  const handleAutomate = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    ev.preventDefault();
    patchTask(
      withMeta(
        { id: task.id, automatic: true, activityDate: new Date() },
        {
          successMessage: 'Task automated',
          errorMessage: 'Error automating task',
        },
      ),
    );
  };

  if (messages.length === 0) return null;

  return (
    <span>
      {formatInSemicolonString(messages)}.
      {canAutomate && (
        <>
          {' '}
          Fix the error and{' '}
          <button
            type="button"
            className="cursor-pointer underline disabled:cursor-default disabled:opacity-60"
            disabled={isPatchTaskLoading}
            onClick={handleAutomate}
          >
            Automate
          </button>{' '}
          the task to try again.
        </>
      )}
    </span>
  );
};
